import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { getStoredSource } from "@/lib/tracking";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) { setError("Vous devez accepter les CGU et la politique de confidentialité."); return; }
    setBusy(true); setError("");
    try {
      await register({ ...form, source: getStoredSource() });
      toast.success("Bienvenue !");
      // Reprise automatique du paiement si une offre a été choisie avant l'inscription
      const pending = sessionStorage.getItem("gestimmony_pending_plan");
      if (pending) {
        sessionStorage.removeItem("gestimmony_pending_plan");
        try {
          const { data } = await api.post("/payments/checkout", {
            lookup_key: pending, origin_url: window.location.origin,
          });
          window.location.href = data.checkout_url;
          return;
        } catch { /* on bascule sur l'onboarding si le checkout échoue */ }
      }
      navigate("/app/onboarding");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <LogoMark className="h-9" />
          <div className="font-heading font-bold text-lg">Gestimmony</div>
        </div>

        <Card className="p-6 border-slate-200 shadow-none">
          <h2 className="font-heading text-2xl font-bold tracking-tight">Créer un compte</h2>
          <p className="mt-1 text-sm text-slate-500">Gérez vos biens en toute sérénité.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Nom complet</Label>
              <Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Mot de passe (min. 6)</Label>
              <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
            </div>
            {error && <div data-testid="register-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-md">{error}</div>}
            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                data-testid="register-accept-terms"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="h-4 w-4 mt-0.5 accent-teal-600"
              />
              <span>
                J'accepte les{" "}
                <Link to="/cgu" target="_blank" className="text-[hsl(var(--primary))] underline">CGU</Link>{" "}
                et la{" "}
                <Link to="/confidentialite" target="_blank" className="text-[hsl(var(--primary))] underline">politique de confidentialité</Link>.
              </span>
            </label>
            <Button type="submit" data-testid="register-submit" disabled={busy || !accepted} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
              {busy ? "Création…" : "Créer mon compte"}
            </Button>
          </form>
        </Card>

        <p className="mt-6 text-sm text-slate-500 text-center">
          Déjà inscrit ? <Link to="/login" className="text-[hsl(var(--primary))] font-medium hover:underline">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}
